import { useState, useMemo, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, FormProvider, useFieldArray } from 'react-hook-form';
import * as yup from 'yup';
import orderBy from 'lodash.orderby';
import { LOCATION_TYPE } from '@features/ComboLocationSelect';
import { VIEW_TYPE, Tab, SystemFieldType } from '@constants/surveys';
import { ProfileName } from '@constants/integrations';
import { accountActions, alertActions, locationsProfilesActions, surveysActions } from '@actions';
import { surveyService } from '@services';
import { useTranslation, useYupValidationResolver, useHasAccessToAsset } from '@hooks';
import { RIGHTS } from '@constants/user';
import PreviewSurvey from '../PreviewSurvey';
import PreviewCustomization from '../PreviewCustomization';
import General from './General';
import Customization from './Customization';
import Questions from './Questions';
import { RadioButtonsWrapper, RadioButtons, Modal, FormWrapper, PreviewWrapper } from './styles';

export const excludedProfileTypes = [
    ProfileName['Booking.com'],
    ProfileName.TheFork,
    ProfileName.Deliveroo,
    ProfileName.iFood,
    ProfileName.CoverManager,
    ProfileName.GoogleOfficial,
    ProfileName.FacebookOfficial,
    ProfileName.YouTube,
    ProfileName.X,
    ProfileName.LinkedIn,
    ProfileName.Pinterest,
    ProfileName.Tiktok,
    ProfileName['Hotels.com'],
    ProfileName.Expedia,
    ProfileName.Apple,
    ProfileName.Zocdoc,
    // Profile Packs
    ProfileName.Foursquare,
    ProfileName.AmazonAlexa,
    ProfileName.Waze,
    ProfileName.Navmii,
    ProfileName.TomTom,
    ProfileName.Uber,
    ProfileName.HERE,
    ProfileName.NetworkPack,
];

const EditModal = ({ modal, onClose, selectedSurvey, brands, onDelete, account }) => {
    const [tab, setTab] = useState(Tab.General);

    const initRef = useRef(0);

    const { data: locationsProfiles } = useSelector(state => state.locationsProfiles);
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { hasAccessToAssetById } = useHasAccessToAsset();

    const rights = hasAccessToAssetById(selectedSurvey);

    const SurveysSchema = yup.object().shape(
        {
            name: yup.string().required('validation.required'),
            customFields: yup.array().of(
                yup.object().shape({
                    name: yup.string().required('validation.required'),
                }),
            ),
            topText: yup.string().when('topText', {
                is: val => val?.length > 0,
                then: yup.string().max(42, 'validation.maxLength'),
                otherwise: yup.string().notRequired(),
            }),
            bottomText: yup.string().when('bottomText', {
                is: val => val?.length > 0,
                then: yup.string().max(42, 'validation.maxLength'),
                otherwise: yup.string().notRequired(),
            }),
            backgroundColor: yup.string().when('backgroundColor', {
                is: val => val?.length > 0,
                then: yup.string().validateColor('validation.hexFormat'),
                otherwise: yup.string().notRequired(),
            }),
            fillColor: yup.string().when('fillColor', {
                is: val => val?.length > 0,
                then: yup.string().validateColor('validation.hexFormat'),
                otherwise: yup.string().notRequired(),
            }),
            message: yup.string().when('message', {
                is: val => !val?.length,
                then: yup.string().notRequired(),
                otherwise: yup.string().required('validation.required'),
            }),
            followUpText: yup.string().when('followUpText', {
                is: val => !val?.length,
                then: yup.string().notRequired(),
                otherwise: yup.string().required('validation.required'),
            }),
        },
        [
            ['topText', 'topText'],
            ['bottomText', 'bottomText'],
            ['backgroundColor', 'backgroundColor'],
            ['fillColor', 'fillColor'],
            ['message', 'message'],
            ['followUpText', 'followUpText'],
        ],
    );

    const resolver = useYupValidationResolver(SurveysSchema);

    const {
        getValues,
        setValue,
        control,
        watch,
        handleSubmit,
        formState: { isValid, isDirty, isSubmitting },
    } = useForm({
        mode: 'all',
        resolver,
        defaultValues: {
            name: '',
            satisfactionSurveyEnabled: false,
            satisfactionSurveyThreshold: 5,
            labels: [],
            reviewPlatforms: [],
            topText: '',
            bottomText: '',
            backgroundColor: '',
            fillColor: '',
            qrLogoUrl: '',
            customizeQrCodeDesign: false,
            landingPageEnable: false,
            socialMediaEnable: false,
            logoUrl: '',
            links: {},
            showReviews: false,
            message: '',
            followUpEnable: false,
            followUpDelay: 7,
            followUpText: '',
            overSurvey: 0,
            contactEmail: false,
            contactEmailRequired: false,
            contactPhone: true,
            contactPhoneRequired: false,
            contactName: true,
            contactNameRequired: false,
            customFields: [],
            locationId: '',
            brandId: '',
            locationType: LOCATION_TYPE.BRAND,
            brandAccess: false,
            valuesAlreadySetToForm: false,
            mainLocationId: null,
            questions: {},
        },
    });

    const { fields, append, remove } = useFieldArray({
        control,
        name: 'customFields',
    });

    const mainLocationId = watch('mainLocationId');
    const questions = watch('questions');

    const availablePlatforms = useMemo(() => {
        return orderBy(
            locationsProfiles.filter(p => !excludedProfileTypes.includes(p.type)),
            ['isInherited', 'type'],
            ['desc', 'asc'],
        );
    }, [mainLocationId, locationsProfiles]);

    const allQuestionsHaveValue = useMemo(
        () =>
            Object.values(questions)
                .flat()
                .every(question => !!question.title && !!question.question),
        [questions],
    );

    useEffect(() => {
        if (mainLocationId) {
            dispatch(locationsProfilesActions.get(mainLocationId));
        }
    }, [mainLocationId]);

    useEffect(() => {
        if (availablePlatforms.length) {
            const reviewPlatforms =
                initRef.current === 0
                    ? selectedSurvey.reviewPlatforms.filter(rP => availablePlatforms.some(aP => aP.type === rP))
                    : availablePlatforms.map(p => p.type);
            setValue('reviewPlatforms', reviewPlatforms);
            initRef.current = 1;
        }
    }, [availablePlatforms]);

    useEffect(() => {
        if (selectedSurvey && modal.open) {
            setValue('name', selectedSurvey.name, { shouldValidate: true });
            const customizationLocationId = selectedSurvey.locationId
                ? selectedSurvey.locationId
                : brands.find(b => b.id === selectedSurvey.brandId)?.locations.find(l => l.isMainLocation)?.id;
            setValue('mainLocationId', customizationLocationId, { shouldValidate: true });
            setValue('satisfactionSurveyEnabled', selectedSurvey.satisfactionSurveyEnabled);
            setValue('satisfactionSurveyThreshold', selectedSurvey.satisfactionSurveyThreshold || 5);
            setValue('labels', selectedSurvey.labels || []);
            setValue('contactEmail', selectedSurvey.systemFields.Email.enable);
            setValue('contactEmailRequired', selectedSurvey.systemFields.Email.required);
            setValue('contactPhone', selectedSurvey.systemFields.Phone.enable);
            setValue('contactPhoneRequired', selectedSurvey.systemFields.Phone.required);
            setValue('contactName', selectedSurvey.systemFields.Name.enable);
            setValue('contactNameRequired', selectedSurvey.systemFields.Name.required);
            setValue('customFields', selectedSurvey.customFields || []);

            setValue('topText', selectedSurvey.topText || '', { shouldValidate: true });
            setValue('bottomText', selectedSurvey.bottomText || '', { shouldValidate: true });
            setValue('backgroundColor', selectedSurvey.backgroundColor || '#fff', {
                shouldValidate: true,
            });
            setValue('fillColor', selectedSurvey.fillColor || '#1f4c5c', { shouldValidate: true });
            setValue('customizeQrCodeDesign', selectedSurvey.customizeQrCodeDesign);
            setValue('qrLogoUrl', selectedSurvey.qrLogoUrl);
            setValue('message', selectedSurvey.message || '', { shouldValidate: true });
            setValue('followUpEnable', selectedSurvey.followUpEnable);
            setValue('followUpDelay', selectedSurvey.followUpDelay || 7);
            setValue('followUpText', selectedSurvey.followUpText || '', { shouldValidate: true });
            setValue('overSurvey', selectedSurvey.overSurvey || 0);
            setValue('questions', selectedSurvey.questions || {});
        }
    }, [selectedSurvey, setValue, modal.open]);

    const isView = modal.type === VIEW_TYPE.VIEW;

    const onSubmit = async () => {
        try {
            const values = getValues();
            await surveyService.editSurvey({
                ...selectedSurvey,
                ...values,
                locationId: values.locationId || null,
                brandId: values.brandId || null,
                systemFields: {
                    Email: {
                        type: SystemFieldType.EMAIL,
                        required: values.contactEmailRequired,
                        enable: values.contactEmail,
                    },
                    Phone: {
                        type: SystemFieldType.PHONE,
                        required: values.contactPhoneRequired,
                        enable: values.contactPhone,
                    },
                    Name: {
                        type: SystemFieldType.NAME,
                        required: values.contactNameRequired,
                        enable: values.contactName,
                    },
                },
                message: values.message,
                followUpText: values.followUpText,
            });
            await dispatch(surveysActions.getAll());
            await dispatch(accountActions.get());
            dispatch(alertActions.success(t('alertMessages.editSuccess')));
            onClose();
        } catch (error) {
            dispatch(alertActions.error(t('alertMessages.editFail')));
        }
    };

    return (
        <Modal
            isOpen={modal.open}
            handleClose={onClose}
            title={t('FeedbackSurveys.modalTitle', { surveyName: selectedSurvey?.name || '' })}
            onPrimaryAction={!rights[RIGHTS.WRITE] ? null : handleSubmit(onSubmit)}
            primaryActionText={t('buttons.save')}
            primaryActionDisabled={isSubmitting || !isValid || !isDirty || !allQuestionsHaveValue}
            onSecondaryAction={onClose}
            secondaryActionText={t('buttons.cancel')}
            onAdditionalAction={!rights[RIGHTS.WRITE] ? null : onDelete}
            additionalTextButton={t('buttons.delete')}
        >
            <FormProvider {...{ getValues, setValue, control, watch, isValid, fields, append, remove }}>
                <FormWrapper>
                    <div>
                        <RadioButtonsWrapper>
                            <RadioButtons
                                value={tab}
                                onChange={setTab}
                                options={[
                                    { value: Tab.General, label: t('FeedbackSurveys.tabSettings') },
                                    { value: Tab.Questions, label: t('common.questions') },
                                    { value: Tab.Customization, label: t('FeedbackSurveys.tabQRandMessages') },
                                ]}
                            />
                        </RadioButtonsWrapper>
                    </div>
                    {tab === Tab.General && (
                        <General
                            isView={isView}
                            brands={brands}
                            selectedSurvey={selectedSurvey}
                            availablePlatforms={availablePlatforms}
                        />
                    )}
                    {tab === Tab.Questions && <Questions account={account} isView={isView} />}
                    {tab === Tab.Customization && (
                        <Customization
                            isView={isView}
                            selectedSurvey={selectedSurvey}
                            locations={account?.companies}
                            brands={brands}
                        />
                    )}
                </FormWrapper>
                <PreviewWrapper>
                    {tab === Tab.General || tab === Tab.Questions ? (
                        <PreviewSurvey tab={tab} account={account} externalId={selectedSurvey?.externalId} />
                    ) : null}
                    {tab === Tab.Customization ? (
                        <PreviewCustomization externalId={selectedSurvey?.externalId} />
                    ) : null}
                </PreviewWrapper>
            </FormProvider>
        </Modal>
    );
};

export default EditModal;
